/**
 * Experience Calc. — the Borrower Experience Workbook as its own drilldown tab
 * (`/loan/:id/experience`, beside Liquidity Calc.; the old
 * `/applicants/experience` path redirects here). Framed like DSCR Calc — a
 * main card on the left, a sticky rollup rail on the right — but the content
 * is what experience-workbook.md asks for:
 *
 *   • the track record — the CARD header keeps Refresh; a toolbar row carries
 *     a search control (a Search BUTTON that swaps to a field when clicked),
 *     the filter switcher (All / Needs attention / Candidates
 *     / Verified) and the two add routes (Extract data → Pull from track
 *     record · Extract a document, both of which confirm first in plain words;
 *     Add item). With nothing on the ledger that row is hidden entirely and
 *     the add routes move into the empty state. The header uses the drilldown
 *     tab header type (`tabTokens.type.pageTitle` / `pageSubtitle`, as Contacts
 *     and Applicants do). Body: the GC qualifying-experience banner, then one
 *     ledger of every ExperienceItem on the loan — what proves it, who it
 *     credits, where it came from, whether UW verified it. Row click opens
 *     the item dialog (attach / edit / verify).
 *   • related parties to scan (hop 1 on this deal, hop 2 across deals) — Open
 *     on a borrower here opens the applicant sidebar in place
 *
 *   RAIL
 *   • the loan's `totalVerifiedExperience` and how many candidates await review
 *   • the shared validation thread with its comment box (only when `/check`
 *     says the caller may) — under the total, so the record of how the number
 *     was reached sits beside the number
 *   • Before verifying — what still blocks persistence: items with no parent,
 *     items with no proof
 *   • By party — every borrower / entity / GC with its own
 *     `verifiedExperienceTotal` (loan fields the backend rewrites after every
 *     mutation, so they are read, never recomputed); the GC's stated
 *     `projectsCompleted` beside the verified count so the gap is visible
 *
 * Shown on every loan, product type and user — no `experienceApplies` gate.
 */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import AddOutlined from "@mui/icons-material/AddOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CloudDownloadOutlined from "@mui/icons-material/CloudDownloadOutlined";
import DocumentScannerOutlined from "@mui/icons-material/DocumentScannerOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { segmentedGroupSx } from "../../../../theme/activeStates";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import { status } from "../../../../theme/colors";
import { FORM, formSx, px } from "../../../../theme/formStyles";
import LiquidityCardSectionHeader from "../LoanDrilldownLiquidityCalculationsTab/LiquidityCardSectionHeader";
import ExperienceChatPanel from "../../experience/ExperienceChatPanel";
import ExperienceExtractDialog from "../../experience/ExperienceExtractDialog";
import ExperienceItemDialog from "../../experience/ExperienceItemDialog";
import ExperienceItemsGrid from "../../experience/ExperienceItemsGrid";
import GcQualifyingExperienceCard from "../../experience/GcQualifyingExperienceCard";
import { ClassificationOverrideSummary } from "../../../../components/borrowerClassification/BorrowerClassificationOverride";
import PullFromTrackRecordDialog from "../../experience/PullFromTrackRecordDialog";
import RelatedPartiesPanel from "../../experience/RelatedPartiesPanel";
import { useExperienceWorkbook } from "../../experience/useExperienceWorkbook";
import {
  ASSOCIATION_TYPES,
  fmtPoints,
  hasParent,
  hasProof,
  listLoanParties,
  needsAttention,
  partiesById,
} from "../../experience/experienceConstants";

/* ─────────────────────────────── the rail ──────────────────────────────── */

/** One bordered card in the rail column. */
const RailCard = ({ children }) => (
  <Box
    sx={{
      border: `1px solid ${FORM.color.divider}`,
      borderRadius: px(FORM.radius.control),
      bgcolor: FORM.color.surface,
      overflow: "hidden",
    }}
  >
    {children}
  </Box>
);

RailCard.propTypes = { children: PropTypes.node };

/**
 * One section inside a rail card — compact: 13/600 title, an optional info
 * tooltip (the explanatory copy lives there, not in the body), an optional
 * right-side action, then the body.
 */
const RailSection = ({ title, info, action, children }) => (
  <Box sx={{ px: px(FORM.s.s4), py: px(FORM.s.s3) }}>
    <Flx ac jb g={1} sx={{ mb: px(FORM.s.s2) }}>
      <Flx ac g={0.5} sx={{ minWidth: 0 }}>
        <Txt sx={{ ...formSx.label, mb: 0, fontWeight: 600 }}>{title}</Txt>
        {info ? (
          <Tooltip title={info} placement="top" arrow>
            <InfoOutlined
              sx={{ fontSize: 14, color: FORM.color.ink4, cursor: "help" }}
            />
          </Tooltip>
        ) : null}
      </Flx>
      {action || null}
    </Flx>
    {children}
  </Box>
);

RailSection.propTypes = {
  title: PropTypes.node,
  info: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
};

/** label left · count right; amber when it's a problem, quiet when zero. */
const CountRow = ({
  label,
  count,
  loaded,
  tone = "warning",
  zeroQuiet = true,
  meta,
  dense = false,
  fmt,
}) => {
  const n = loaded ? count : null;
  const color =
    n == null
      ? FORM.color.ink4
      : n > 0
        ? status[tone]?.fg || FORM.color.ink
        : zeroQuiet
          ? FORM.color.ink4
          : FORM.color.ink;
  return (
    <Flx ac jb g={1} sx={{ minHeight: dense ? 23 : 26 }}>
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: px(dense ? FORM.s.glue : FORM.s.s2),
        }}
      >
        {label}
      </Box>
      <Txt
        sx={{
          ...formSx.label,
          ...(dense ? { fontSize: 12, lineHeight: "16px" } : null),
          mb: 0,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color,
          flexShrink: 0,
          // The count and its "/ stated" suffix are ONE reading — without this
          // the suffix wraps under the number when the label side is wide
          // (a long contractor name pushed "/ 2" onto its own line).
          whiteSpace: "nowrap",
        }}
      >
        {n == null ? "—" : fmt ? fmt(n) : n}
        {meta}
      </Txt>
    </Flx>
  );
};

CountRow.propTypes = {
  label: PropTypes.node,
  count: PropTypes.number,
  loaded: PropTypes.bool,
  tone: PropTypes.string,
  zeroQuiet: PropTypes.bool,
  meta: PropTypes.node,
  /** Render the number (points get one decimal). */
  fmt: PropTypes.func,
  /** Compact type + tighter rows — the By party list, which can run long. */
  dense: PropTypes.bool,
};

const PARTY_SHORT = {
  [ASSOCIATION_TYPES.BORROWERS]: "BRWR",
  [ASSOCIATION_TYPES.ENTITIES]: "ENT",
  [ASSOCIATION_TYPES.GENERAL_CONTRACTORS]: "GC",
};
const PARTY_ROWS_COLLAPSED = 3;

const BEFORE_VERIFYING_INFO =
  "Verifying rolls the item onto the deal and persists it to its parents' shared records with its proof. An item with no parent persists nowhere. A near-match on identity is queued for you to confirm — never merged silently.";
const BY_PARTY_INFO =
  "Verified items crediting each party — the loan's own rollup, rewritten after every change. A contractor also shows the projects its profile states, so the gap is visible.";

const ExperienceRail = ({
  loan,
  items,
  loaded,
  totalVerified,
  onOpenApplicants,
  dealId,
  showChat,
  onChatForbidden,
}) => {
  const parties = useMemo(() => listLoanParties(loan), [loan]);
  const [showAllParties, setShowAllParties] = useState(false);
  const candidates = items.filter((it) => !it?.verified).length;
  const noParent = items.filter((it) => !hasParent(it)).length;
  const noProof = items.filter((it) => !hasProof(it)).length;
  const excluded = items.filter(
    (it) => it?.verified && it?.countedForExperience === false,
  ).length;
  const gcById = useMemo(
    () =>
      new Map((loan?.generalContractors || []).map((g) => [String(g?._id), g])),
    [loan?.generalContractors],
  );
  // Computed by the backend on every read (RTL-shaped products only); it is
  // never persisted, so there is nothing to refresh — it arrives with the loan.
  const classification = loan?.borrowerClassificationDetails || null;
  const visibleParties = showAllParties
    ? parties
    : parties.slice(0, PARTY_ROWS_COLLAPSED);
  const hiddenParties = parties.length - visibleParties.length;

  const dividerSx = { borderColor: FORM.color.divider };
  // The rail's list rows run one size below a form label so a long party name
  // fits on its line — every list in the card shares it.
  const rowLabelSx = {
    ...formSx.label,
    fontSize: 12,
    lineHeight: "16px",
    mb: 0,
    fontWeight: 400,
    color: FORM.color.ink2,
  };

  return (
    <Flx column gap={2}>
      {/* the rollup — total · what blocks verifying · per party, one compact card */}
      <RailCard>
        <RailSection
          title="Verified experience"
          info={`Verified and counted items on the loan${excluded ? ` (${excluded} verified but excluded)` : ""}. Candidates don't count until UW verifies them.`}
        >
          <Flx g={0.75} sx={{ alignItems: "baseline" }}>
            <Txt
              sx={{
                fontSize: 26,
                lineHeight: "30px",
                fontWeight: 600,
                color: status.success.fg,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {loaded ? fmtPoints(totalVerified) : "—"}
            </Txt>
            <Txt sx={formSx.meta}>
              of {loaded ? items.length : "—"} on the loan
              {loaded && candidates ? (
                <Box component="span" sx={{ color: status.warning.fg }}>
                  {" "}
                  · {candidates} awaiting review
                </Box>
              ) : null}
            </Txt>
          </Flx>
        </RailSection>
        <Divider sx={dividerSx} />

        {/* What actually qualifies — the sum classification scores. Present
            only on RTL-shaped products, where the backend computes
            `borrowerClassificationDetails` on every read. */}
        {classification && (
          <>
            <RailSection
              title="Qualifying experience"
              info={
                (classification.experienceBasis ||
                  "Guarantor verified experience plus the loan-root GC total") +
                ". Borrower classification scores this SUM, not the guarantor number on its own."
              }
              action={
                classification.borrowerClass ? (
                  <Box component="span" sx={formSx.pill("info")}>
                    Class {classification.borrowerClass}
                  </Box>
                ) : null
              }
            >
              <Flx g={0.75} sx={{ alignItems: "baseline", mb: px(FORM.s.s1) }}>
                <Txt
                  sx={{
                    fontSize: 22,
                    lineHeight: "26px",
                    fontWeight: 600,
                    color: FORM.color.ink,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtPoints(classification.totalQualifyingExperience)}
                </Txt>
                <Txt sx={formSx.meta}>qualifying</Txt>
              </Flx>
              <CountRow
                dense
                label={<Txt sx={rowLabelSx}>Guarantors</Txt>}
                count={Number(classification.borrowerVerifiedExperience) || 0}
                loaded
                tone="success"
                fmt={fmtPoints}
              />
              <CountRow
                dense
                label={<Txt sx={rowLabelSx}>General contractors</Txt>}
                count={Number(classification.gcVerifiedExperience) || 0}
                loaded
                tone="success"
                fmt={fmtPoints}
              />
              {classification.status !== "calculated" &&
                classification.reason && (
                  <Txt sx={{ ...formSx.helper, mt: px(FORM.s.s2) }}>
                    Classification incomplete — {classification.reason}.
                  </Txt>
                )}
              {/* calculated vs approved override, its justification note and
                  the exception-backing chip; the Override button for UW / admin */}
              <Box sx={{ mt: px(FORM.s.s3) }}>
                <ClassificationOverrideSummary />
              </Box>
            </RailSection>
            <Divider sx={dividerSx} />
          </>
        )}

        <RailSection title="Before verifying" info={BEFORE_VERIFYING_INFO}>
          <CountRow
            dense
            label={<Txt sx={rowLabelSx}>Items with no parent</Txt>}
            count={noParent}
            loaded={loaded}
            zeroQuiet={false}
          />
          <CountRow
            dense
            label={<Txt sx={rowLabelSx}>Items with no proof</Txt>}
            count={noProof}
            loaded={loaded}
            zeroQuiet={false}
          />
        </RailSection>
        <Divider sx={dividerSx} />

        <RailSection
          title="By party"
          info={BY_PARTY_INFO}
          action={
            <Box
              component="button"
              type="button"
              onClick={onOpenApplicants}
              sx={{
                ...formSx.link,
                fontSize: FORM.type.meta[0],
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: px(3),
                background: "none",
                border: 0,
                p: 0,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Open applicants <OpenInNewOutlined sx={{ fontSize: 12 }} />
            </Box>
          }
        >
          {parties.length ? (
            <Box>
              {visibleParties.map((p) => {
                const gc =
                  p.type === ASSOCIATION_TYPES.GENERAL_CONTRACTORS
                    ? gcById.get(p.id)
                    : null;
                return (
                  <CountRow
                    key={`${p.type}-${p.id}`}
                    dense
                    label={
                      <>
                        <Box
                          component="span"
                          sx={{
                            ...formSx.pill("neutral"),
                            height: 16,
                            width: 40,
                            justifyContent: "center",
                            flexShrink: 0,
                            letterSpacing: ".04em",
                            fontSize: 9.5,
                          }}
                        >
                          {PARTY_SHORT[p.type]}
                        </Box>
                        <Txt
                          sx={{
                            ...formSx.label,
                            fontSize: 12,
                            lineHeight: "16px",
                            mb: 0,
                            fontWeight: 400,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {p.name}
                        </Txt>
                      </>
                    }
                    count={p.verifiedExperienceTotal ?? 0}
                    loaded={loaded}
                    tone="success"
                    fmt={fmtPoints}
                    meta={
                      gc && gc.projectsCompleted != null ? (
                        <Box
                          component="span"
                          sx={{
                            ...formSx.meta,
                            fontSize: 11,
                            fontWeight: 400,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {" / "}
                          {gc.projectsCompleted}
                        </Box>
                      ) : null
                    }
                  />
                );
              })}
              {hiddenParties > 0 || showAllParties ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowAllParties((v) => !v)}
                  sx={{
                    ...formSx.meta,
                    color: FORM.color.ink3,
                    background: "none",
                    border: 0,
                    p: 0,
                    mt: px(4),
                    cursor: "pointer",
                    fontFamily: "inherit",
                    "&:hover": { color: FORM.color.ink },
                  }}
                >
                  {showAllParties ? "Show fewer" : `+${hiddenParties} more`}
                </Box>
              ) : null}
            </Box>
          ) : (
            <Txt sx={formSx.helper}>
              No borrowers, entities or contractors on the loan yet.
            </Txt>
          )}
        </RailSection>
      </RailCard>

      {/* the shared validation thread — its own card, last */}
      {showChat ? (
        <RailCard>
          <RailSection
            title="Validation thread"
            info="Shared with everyone on the loan. The assistant advises; it never edits a row."
          >
            <ExperienceChatPanel
              dealId={dealId}
              onForbidden={onChatForbidden}
            />
          </RailSection>
        </RailCard>
      ) : null}
    </Flx>
  );
};

ExperienceRail.propTypes = {
  loan: PropTypes.object,
  items: PropTypes.array,
  loaded: PropTypes.bool,
  totalVerified: PropTypes.number,
  onOpenApplicants: PropTypes.func,
  dealId: PropTypes.string,
  showChat: PropTypes.bool,
  onChatForbidden: PropTypes.func,
};

/* ─────────────────────────────── toolbar ───────────────────────────────── */

/** The three track-record actions — compact next to the filter switcher. */
/**
 * The two ways rows arrive from paperwork, behind one menu button. Both end in
 * the same seed-merge on the backend; they differ in scope — everything the
 * pipeline has already parsed, or one document you pick and extract now.
 */
const IMPORT_ROUTES = [
  {
    key: "pull",
    label: "Pull from track record",
    helper:
      "Read the schedules already parsed on this loan and add every property they list.",
    icon: CloudDownloadOutlined,
  },
  {
    key: "extract",
    label: "Extract a document",
    helper:
      "Pick one document, run the extractor on it, then add what it finds.",
    icon: DocumentScannerOutlined,
  },
];

/** The header actions — the form system's button height (38px). */
const TOOLBAR_BUTTON_SX = formSx.button;
const FILTERS = [
  { key: "all", label: "All", test: () => true },
  {
    key: "attention",
    label: "Needs attention",
    test: (it) => needsAttention(it),
    dot: true,
    // Only worth a chip when something actually needs attention.
    hideWhenEmpty: true,
  },
  { key: "candidates", label: "Candidates", test: (it) => !it?.verified },
  { key: "verified", label: "Verified", test: (it) => !!it?.verified },
];

const FilterChips = ({ items, value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    onChange={(_e, v) => v && onChange(v)}
    sx={{
      ...segmentedGroupSx({ fontSize: 12.5 }),
      border: `1px solid ${FORM.color.divider}`,
    }}
  >
    {FILTERS.map((f) => {
      const n = items.filter(f.test).length;
      if (f.hideWhenEmpty && n === 0) return null;
      return (
        <ToggleButton
          key={f.key}
          value={f.key}
          sx={{ display: "flex", alignItems: "center", gap: px(6) }}
        >
          {f.dot && n ? (
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: status.warning.solid,
              }}
            />
          ) : null}
          {f.label}
          <Box
            component="span"
            sx={{
              ...formSx.meta,
              color: f.dot && n ? status.warning.fg : FORM.color.ink4,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {n}
          </Box>
        </ToggleButton>
      );
    })}
  </ToggleButtonGroup>
);

FilterChips.propTypes = {
  items: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

/* ─────────────────────────────────── page ───────────────────────────────── */

const LoanDrilldownExperienceTab = () => {
  const navigate = useNavigate();
  const loan = useSelector((s) => s.loanDrilldown);
  const dealId = loan?._id;
  const wb = useExperienceWorkbook(dealId);
  const [dialog, setDialog] = useState(null); // { item } | null
  const [extractOpen, setExtractOpen] = useState(false);
  const [pullOpen, setPullOpen] = useState(false);
  const [importAnchor, setImportAnchor] = useState(null);
  const [chatForbidden, setChatForbidden] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const byId = useMemo(() => partiesById(loan), [loan]);
  const busyAny = !!wb.busy;
  const showChat = wb.experienceChatEnabled && !chatForbidden;
  const isInitialLoading = !wb.loaded && wb.loading;

  // The Needs-attention chip disappears once nothing needs attention; if it
  // was the active filter, fall back to All rather than an empty, chip-less list.
  const attentionCount = useMemo(
    () => (wb.items || []).filter(needsAttention).length,
    [wb.items],
  );
  useEffect(() => {
    if (filter === "attention" && attentionCount === 0) setFilter("all");
  }, [filter, attentionCount]);

  const visibleItems = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    return (wb.items || []).filter(f.test);
  }, [wb.items, filter]);

  // The hook already toasts success / failure; on failure the dialog stays
  // open so the user can retry or cancel.
  const confirmPull = async () => {
    try {
      await wb.populate();
      setPullOpen(false);
    } catch (_err) {
      /* toast shown by the hook */
    }
  };

  // The three actions sit in the section header (right of the title); the
  // second row is the filter switcher left, the search right — always, so the
  // empty state below stays a plain notice.
  // Search is a BUTTON until it's wanted — the ledger is short enough that a
  // permanent field is mostly empty chrome. Clicking it swaps in the input
  // (focused); leaving it empty on blur puts the button back, so an abandoned
  // search doesn't leave a stray field behind. A live query keeps the field
  // open, with a clear affordance.
  const searchBox = searchOpen ? (
    <TextField
      size="small"
      autoFocus
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      onBlur={() => {
        if (!query.trim()) setSearchOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setQuery("");
          setSearchOpen(false);
        }
      }}
      placeholder="Search address or party"
      sx={{ ...formSx.input, width: 240 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchOutlined sx={{ fontSize: 18 }} />
          </InputAdornment>
        ),
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQuery("");
                setSearchOpen(false);
              }}
              sx={{ color: FORM.color.ink4 }}
            >
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  ) : (
    <Button
      variant="outlined"
      color="gray"
      startIcon={<SearchOutlined sx={{ fontSize: 16 }} />}
      onClick={() => setSearchOpen(true)}
      sx={TOOLBAR_BUTTON_SX}
    >
      Search
    </Button>
  );

  // The two "bring rows in from documents" routes are one button with a menu —
  // they answer the same question (where do the properties come from?) and
  // differ only in scope: everything already parsed, or one document you pick.
  // The card header keeps only Refresh; the two ways rows arrive live on the
  // toolbar row, beside the filter switcher the search box now leads.
  const refreshAction = (
    <Button
      variant="outlined"
      color="gray"
      startIcon={
        wb.loading ? (
          <CircularProgress size={14} />
        ) : (
          <SyncOutlined sx={{ fontSize: 16 }} />
        )
      }
      onClick={() => wb.reload()}
      disabled={wb.loading}
      sx={TOOLBAR_BUTTON_SX}
    >
      Refresh
    </Button>
  );
  // Right-aligned on the toolbar row; centred when the empty state hosts it.
  const rowActions = (alignCenter = false) => (
    <Flx
      ac
      g={0.75}
      wrap
      sx={{ justifyContent: alignCenter ? "center" : "flex-end" }}
    >
      <Button
        variant="outlined"
        color="gray"
        startIcon={
          wb.busy === "populate" ? (
            <CircularProgress size={14} />
          ) : (
            <CloudDownloadOutlined sx={{ fontSize: 16 }} />
          )
        }
        endIcon={<ExpandMoreRounded sx={{ fontSize: 18 }} />}
        onClick={(e) => setImportAnchor(e.currentTarget)}
        disabled={busyAny}
        aria-haspopup="menu"
        aria-expanded={!!importAnchor}
        sx={TOOLBAR_BUTTON_SX}
      >
        Extract data
      </Button>
      <Menu
        anchorEl={importAnchor}
        open={!!importAnchor}
        onClose={() => setImportAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { py: px(FORM.s.s1) } }}
        PaperProps={{
          sx: {
            mt: px(FORM.s.s1),
            width: 320,
            borderRadius: px(FORM.radius.control),
            border: `1px solid ${FORM.color.divider}`,
            boxShadow: "0 8px 28px rgba(15,23,42,.14)",
          },
        }}
      >
        {IMPORT_ROUTES.map((r) => (
          <MenuItem
            key={r.key}
            onClick={() => {
              setImportAnchor(null);
              if (r.key === "pull") setPullOpen(true);
              else setExtractOpen(true);
            }}
            sx={{
              alignItems: "flex-start",
              gap: px(FORM.s.s3),
              py: px(FORM.s.s3),
              px: px(FORM.s.s4),
            }}
          >
            <r.icon
              sx={{
                fontSize: 18,
                color: FORM.color.ink3,
                mt: px(1),
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Txt sx={{ ...formSx.label, mb: 0, display: "block" }}>
                {r.label}
              </Txt>
              <Txt
                sx={{
                  ...formSx.helper,
                  mt: 0,
                  display: "block",
                  whiteSpace: "normal",
                }}
              >
                {r.helper}
              </Txt>
            </Box>
          </MenuItem>
        ))}
      </Menu>
      <Button
        startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
        onClick={() => setDialog({ item: null })}
        disabled={busyAny}
        sx={TOOLBAR_BUTTON_SX}
      >
        Add item
      </Button>
    </Flx>
  );

  // search · filter switcher on the left, the two add routes on the right.
  const toolbar = (
    <Flx ac jb g={1.5} wrap sx={{ mb: px(FORM.s.s4) }}>
      <Flx ac g={1} wrap sx={{ minWidth: 0 }}>
        {searchBox}
        <FilterChips
          items={wb.items || []}
          value={filter}
          onChange={setFilter}
        />
      </Flx>
      {rowActions()}
    </Flx>
  );

  const hasItems = (wb.items || []).length > 0;

  // With nothing on the ledger there is nothing to filter or search, so the
  // whole toolbar row is hidden and the two add routes move into the empty
  // state — the only thing there is to do from here.
  const emptyContent =
    wb.loaded && !hasItems ? (
      <EmptyStatePlaceholder
        icon={<WorkspacePremiumOutlined />}
        title="No track record yet"
        description="Pull the properties from the borrower's track-record paperwork, extract a specific schedule or closing statement, or add a property by hand. Nothing counts until underwriting verifies it."
        py={6}
        actions={rowActions(true)}
      />
    ) : (
      <Txt sx={{ ...formSx.helper, display: "block", p: px(FORM.s.s5) }}>
        No items match this filter.
      </Txt>
    );

  return (
    <Box sx={{ py: 4, px: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 2.5fr) minmax(0, 1fr)",
          },
          gap: 3,
          maxWidth: "1600px",
          mx: "auto",
        }}
      >
        <Flx column gap={3} sx={{ minWidth: 0 }}>
          <PrimaryContentCard
            loading={isInitialLoading}
            title="Experience & Track Record"
            subTitle="The borrower's track record as line items — each property with what proves it, who it credits, and whether underwriting verified it. Verified items roll onto the loan and travel with the borrower to future loans."
            cardSx={{ position: "relative" }}
            titleSx={T.type.pageTitle}
            headerSx={{
              pr: 46,
              // The actions are absolutely positioned, so the title block would
              // otherwise run its full width and slide under them.
              "& > div:first-of-type": { maxWidth: 760 },
              "& .MuiTypography-root:not(h1):not(h2)": T.type.pageSubtitle,
            }}
          >
            {/* the card header keeps Refresh; the add routes are on the toolbar row */}
            <Box sx={{ position: "absolute", top: 22, right: 28 }}>
              {refreshAction}
            </Box>
            <Divider sx={{ mt: 0.5, mb: 5, borderWidth: "1.5px" }} />

            <Flx column g={0} sx={{ px: 1 }}>
              {/* GC qualifying experience — the loan-root
                  totalVerifiedGCExperience the rollup PROPOSES after each
                  verify and underwriting applies (HITL). Self-hiding: nothing
                  renders until there is a proposal or an applied value. Keyed
                  on the verified total so a verify re-reads the queue — the
                  verify response itself does not carry the proposal. */}
              <Box sx={{ mb: px(FORM.s.s5) }}>
                <GcQualifyingExperienceCard
                  compact
                  refreshKey={`${(wb.items || []).length}:${wb.totalVerifiedExperience}`}
                />
              </Box>

              {/* ledger */}
              <Box>
                <LiquidityCardSectionHeader
                  title={`Track record (${(wb.items || []).length})`}
                  subTitle="Each row is a property the borrower, an entity or a contractor did. Attach proof and a parent, then verify. Click a row to open it."
                  sx={{ mb: 2 }}
                />
                {hasItems ? toolbar : null}
                <ExperienceItemsGrid
                  items={visibleItems}
                  loan={loan}
                  byId={byId}
                  activeItemId={dialog?.item?._id}
                  onRowClick={(item) => setDialog({ item })}
                  quickFilterText={query}
                  emptyContent={emptyContent}
                />
                {wb.loaded && (wb.items || []).length ? (
                  <Flx ac jb g={2} sx={{ px: 0.5, pt: px(FORM.s.s3) }}>
                    <Txt sx={formSx.meta}>
                      {visibleItems.length === wb.items.length
                        ? `${wb.items.length} item${wb.items.length === 1 ? "" : "s"}`
                        : `${visibleItems.length} of ${wb.items.length} items`}{" "}
                      · {wb.items.filter((it) => it?.verified).length} verified
                    </Txt>
                    <Txt sx={formSx.meta}>
                      counted toward experience{" "}
                      <b style={{ color: FORM.color.ink }}>
                        {wb.totalVerifiedExperience}
                      </b>
                    </Txt>
                  </Flx>
                ) : null}
              </Box>

              <Divider sx={{ my: 6, mx: -2 }} />

              {/* related parties */}
              <Box>
                <LiquidityCardSectionHeader
                  title="Related parties to scan"
                  subTitle="Co-borrowers, entities and entity members on this deal (hop 1), and the parties they share other deals with across both LOS (hop 2). Open one to pull their track record in too."
                />
                <RelatedPartiesPanel dealId={dealId} />
              </Box>
            </Flx>
          </PrimaryContentCard>
        </Flx>

        <Box
          sx={{
            position: { xs: "static", md: "sticky" },
            top: 24,
            alignSelf: "flex-start",
            minWidth: 0,
          }}
        >
          <ExperienceRail
            loan={loan}
            items={wb.items || []}
            loaded={wb.loaded}
            totalVerified={wb.totalVerifiedExperience}
            onOpenApplicants={() => navigate(`/loan/${dealId}/applicants`)}
            dealId={dealId}
            showChat={showChat}
            onChatForbidden={() => setChatForbidden(true)}
          />
        </Box>
      </Box>

      <ExperienceItemDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        item={dialog?.item || null}
        loan={loan}
        busy={wb.busy}
        onCreate={wb.createItem}
        onUpdate={wb.updateItem}
        onVerify={wb.verifyItem}
        onRemove={wb.removeItem}
        onExtract={() => {
          setDialog(null);
          setExtractOpen(true);
        }}
      />
      <ExperienceExtractDialog
        open={extractOpen}
        onClose={() => setExtractOpen(false)}
        loan={loan}
        busy={wb.busy}
        onExtract={wb.extractDocument}
      />
      <PullFromTrackRecordDialog
        open={pullOpen}
        onClose={() => setPullOpen(false)}
        onConfirm={confirmPull}
        busy={wb.busy === "populate"}
        itemCount={(wb.items || []).length}
      />
    </Box>
  );
};

export default LoanDrilldownExperienceTab;
